import { JSX, useRef, useState, FocusEvent } from 'react';
import { NavLink, useLocation } from 'react-router';

export interface NavItemProps {
  label: string;
  icon?: JSX.Element;
  to: string;
  isActive?: boolean;
  isParent?: boolean;
  isCollapsed?: boolean;
  subMenu?: NavItemProps[];
  onClick?: () => void;
}

const NavItem = ({
  label,
  icon,
  to,
  isActive,
  isParent = false,
  isCollapsed,
  subMenu,
  onClick,
}: NavItemProps) => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);

  const showTooltip = isCollapsed && (isHovered || isDetailsOpen);

  const handleBlur = (e: FocusEvent) => {
    if (
      !containerRef.current?.contains(e.relatedTarget as Node) &&
      isCollapsed
    ) {
      setIsDetailsOpen(false);
    }
  };

  return (
    <li
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onBlur={handleBlur}
    >
      {subMenu ? (
        <>
          <details open={isDetailsOpen}>
            <summary
              className={`flex items-center justify-between gap-2 
                ${isCollapsed ? 'justify-center' : ''} 
                ${(isActive || isHovered) && isParent ? 'md:bg-blue-50' : ''}
              `}
              onClick={(e) => {
                e.preventDefault();
                setIsDetailsOpen(!isDetailsOpen);
              }}
            >
              <div
                className={`flex items-center gap-2 ${
                  isActive && !isParent
                    ? 'md:text-secondary md:font-semibold'
                    : ''
                }`}
              >
                {icon}
                {!isCollapsed && <span>{label}</span>}
              </div>
            </summary>
            {!isCollapsed && isDetailsOpen && (
              <ul
                className={`max-h-[60vh] overflow-y-auto pr-2 md:max-h-none md:overflow-visible
      ${isParent ? 'scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400' : ''}
    `}
              >
                {subMenu.map((item) => (
                  <div key={`submenu-${item.to}`}>
                    <NavItem
                      key={item.to}
                      label={item.label}
                      icon={item.icon}
                      to={item.to}
                      isActive={location.pathname.includes(item.to)}
                      isCollapsed={isCollapsed}
                      subMenu={item.subMenu}
                      onClick={item.onClick}
                    />
                  </div>
                ))}
              </ul>
            )}
          </details>

          {showTooltip && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 z-50 ml-3 bg-primary text-white text-sm px-3 py-1 rounded shadow">
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rotate-45 z-50" />
              <span>{label}</span>
              <hr className="border-l-1 h-3/4 ml-8" />
              <ul
                className={`max-h-[60vh] overflow-y-auto pr-2 md:max-h-none md:overflow-visible
      ${isParent ? 'scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400' : ''}
    `}
              >
                {subMenu.map((item) => (
                  <div key={`tooltip-submenu-${item.to}`}>
                    <NavItem
                      key={item.to}
                      label={item.label}
                      to={item.to}
                      subMenu={item.subMenu}
                      onClick={item.onClick}
                    />
                  </div>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          <NavLink
            to={to}
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors 
              ${isCollapsed ? 'justify-center' : ''}
              ${isActive && !isParent ? 'text-secondary font-semibold' : ''}
              ${isActive && isParent ? 'bg-blue-50' : ''}
            `}
          >
            {icon}
            {!isCollapsed && <span>{label}</span>}
          </NavLink>

          {showTooltip && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 z-50 ml-3 bg-primary text-white text-sm px-3 py-1 rounded shadow">
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rotate-45 z-50" />
              {label}
            </div>
          )}
        </>
      )}
    </li>
  );
};

export default NavItem;
